import type { CalendarDate } from "@internationalized/date";
import { type ReactElement, useRef, useState } from "react";
import type { CalendarHandle } from "../calendar/use-calendar-handle";
import { useDateSelection } from "./use-date-selection";
import { useCalendarScroll } from "./use-calendar-scroll";
import { DateInput } from "./date-input";
import { Dropdown, useDropdown } from "./dropdown";
import { MobileView } from "./mobile-view";
import { FieldError } from "../field-error";
import { useMatchMedia } from "../../hooks/use-match-media";
import type { RenderDate } from "../calendar/day";
import type { Tuple } from "../../types";
import { useBlockUnderlayScrollWhileOpen } from "./use-block-underlay-scroll-while-open";
import { AnnounceSelectedDate } from "./announce-selected-date";
import { BREAKPOINT_MD } from "@jetblue/kit-tailwind-config/theme";
import { cn } from "../../utilities/tailwind";

export type FocusState = "start" | "end" | "none";
export type RangeDatePickerLabels = {
  close: string;
  depart: string;
  departError: string;
  done: string;
  reset: string;
  return: string;
  returnError: string;
};
export type RangeDatePickerProperties = {
  className?: string | undefined;
  departIsRequired?: boolean | undefined;
  departName?: string | undefined;
  endDate: CalendarDate;
  footerSlot?: ReactElement | undefined;
  headingSlot?: ReactElement | undefined;
  labels: RangeDatePickerLabels;
  onFocusStateChange?: ((focusState: FocusState) => void) | undefined;
  renderDate?: RenderDate;
  returnIsRequired?: boolean | undefined;
  returnName?: string | undefined;
  selectedDates: Tuple<CalendarDate | undefined>;
  setSelectedDates: Tuple<(date?: CalendarDate) => void>;
  showValidation?: boolean | undefined;
  startDate: CalendarDate;
};
export const RangeDatePicker = ({
  className,
  departIsRequired,
  departName,
  endDate,
  footerSlot,
  headingSlot,
  labels,
  onFocusStateChange,
  renderDate,
  returnIsRequired,
  returnName,
  selectedDates,
  setSelectedDates,
  startDate,
  showValidation,
}: RangeDatePickerProperties) => {
  const [isOpen, setIsOpen] = useState(false);
  const { target, floating, refs } = useDropdown({
    isOpen,
    onOpenChange: setIsOpen,
  });
  const endReference = useRef<HTMLInputElement>(null);
  const startReference = useRef<HTMLInputElement>(null);
  const [focusState, setFocusState] = useState<FocusState>("none");

  const onFocusStateUpdate = (nextFocusState: FocusState) => {
    setFocusState(nextFocusState);
    onFocusStateChange?.(nextFocusState);
  };

  const setStartFocus = (focused: boolean) => {
    onFocusStateUpdate(focused ? "start" : "none");
  };

  const setEndFocus = (focused: boolean) => {
    onFocusStateUpdate(focused ? "end" : "none");
  };

  const twClassName = cn(
    "flex w-full max-w-full flex-row gap-1 lg:max-w-96",
    className,
  );
  const desktopCalendarReference = useRef<CalendarHandle | null>(null);
  const mobileCalendarReference = useRef<CalendarHandle | null>(null);

  // Use custom hook for date selection logic
  const onDateClick = useDateSelection(
    selectedDates,
    setSelectedDates,
    focusState,
    endReference,
  );
  // IMPORTANT: The media query below must be kept in sync with the one used in the MobileView component.
  // Both use the same breakpoint (md) to determine mobile vs desktop behavior.
  // If these queries ever differ by even 1px, the DateInput may become readonly at a different width than MobileView switches to mobile layout.
  // This causes a confusing user experience: the input could be readonly while the calendar is still in desktop mode, or vice versa.
  // Always update both places together to ensure consistent breakpoints and avoid off-by-one errors.
  const isMobile = !useMatchMedia(`(min-width: ${BREAKPOINT_MD})`);
  const activeCalendarReference = isMobile
    ? mobileCalendarReference
    : desktopCalendarReference;

  // Use custom hooks for calendar scroll behavior
  useCalendarScroll(
    isOpen,
    isMobile,
    focusState,
    selectedDates,
    activeCalendarReference,
  );

  useBlockUnderlayScrollWhileOpen({ isMobile, isOpen });

  return (
    <div {...target} className={twClassName} ref={refs.setReference}>
      <AnnounceSelectedDate selectedDate={selectedDates[0]} />
      <AnnounceSelectedDate selectedDate={selectedDates[1]} />
      <DateInput
        closeCalendar={() => {
          setIsOpen(false);
        }}
        error={
          <FieldError>
            {({ isInvalid }) => (isInvalid ? labels.departError : "")}
          </FieldError>
        }
        isCalendarOpen={isOpen}
        isRequired={departIsRequired}
        label={labels.depart}
        max={selectedDates[1] ?? endDate}
        min={startDate}
        name={departName}
        onChange={setSelectedDates[0]}
        onFocus={() => {
          setIsOpen(true);
        }}
        onFocusChange={setStartFocus}
        readOnly={isMobile}
        ref={startReference}
        showValidation={showValidation}
        value={selectedDates[0]}
      />
      <DateInput
        closeCalendar={() => {
          setIsOpen(false);
        }}
        error={
          <FieldError>
            {({ isInvalid }) => (isInvalid ? labels.returnError : "")}
          </FieldError>
        }
        isCalendarOpen={isOpen}
        isRequired={returnIsRequired}
        label={labels.return}
        max={endDate}
        min={selectedDates[0] ?? startDate}
        name={returnName}
        onChange={setSelectedDates[1]}
        onFocus={() => {
          setIsOpen(true);
        }}
        onFocusChange={setEndFocus}
        readOnly={isMobile}
        ref={endReference}
        showValidation={showValidation}
        value={selectedDates[1]}
      />
      <Dropdown
        calendarRef={desktopCalendarReference}
        endDate={endDate}
        footerSlot={footerSlot}
        highlightRange
        isOpen={isOpen}
        labels={labels}
        onDateClick={onDateClick}
        onDone={() => {
          setIsOpen(false);
        }}
        rangeEnd={selectedDates[1]}
        rangeStart={selectedDates[0]}
        ref={refs.setFloating}
        renderDate={renderDate}
        startDate={startDate}
        {...floating}
      />
      <MobileView
        calendarRef={mobileCalendarReference}
        dateInputConfigs={[
          {
            focused: focusState === "start",
            label: labels.depart,
            onFocus: () => {
              setStartFocus(true);
            },
            value: selectedDates[0],
          },
          {
            focused: focusState === "end",
            label: labels.return,
            onFocus: () => {
              setEndFocus(true);
            },
            value: selectedDates[1],
          },
        ]}
        endDate={endDate}
        footerSlot={footerSlot}
        headingSlot={headingSlot}
        highlightRange
        isOpen={isOpen}
        labels={labels}
        onCloseClick={() => {
          setIsOpen(false);
        }}
        onDateClick={onDateClick}
        onResetClick={() => {
          setSelectedDates[0]();
          setSelectedDates[1]();
        }}
        renderDate={renderDate}
        startDate={startDate}
      />
    </div>
  );
};
RangeDatePicker.displayName = "DatePicker.Range";
